'use client';

/**
 * useEventLeadGate — 이벤트 페이지 세션 기반 라우팅 게이트
 *
 * 세션 스토리지의 `ff_event_leads` 배열에 slug를 저장해
 * contact 페이지 ↔ 결과 페이지 사이의 분기를 처리한다.
 *
 * ## 기본 동작 (rules 미지정 시)
 * - contact 페이지 진입 + slug가 이미 완료 → 결과 페이지로 replace
 * - 결과 페이지 진입 + slug가 미완료       → contact 페이지로 replace
 *
 * ## QA/검수용 게이트 우회
 * - **공통**: URL에 `?mktad` 가 있으면 세션 없이 결과 페이지 확인 가능 (모든 이벤트 공통).
 * - **이벤트별**: `bypassParam: 'mktad'` 등으로 추가 우회 파라미터 지정 가능 (`?mktad=<양수>`).
 * `isLeadBypassed`가 true이면 default rules는 건너뛴다.
 * 커스텀 rules에서는 `!isLeadBypassed` 조건으로 리다이렉트를 막아야 한다.
 *
 * ## 커스텀 규칙
 * ```tsx
 * const gate = useEventLeadGate({
 *   slug: 'lowest-price',
 *   bypassParam: 'mktad',
 *   rules: [
 *     {
 *       when: ({ isLeadBypassed, searchParams, pathname }) => ...,
 *       to: ({ searchParams }) => `/event/ff-lowest-price-submit?mktad=${searchParams.get('mktad')}`,
 *     },
 *   ],
 * });
 * ```
 * - rules는 위에서부터 순서대로 평가하며 첫 번째 when=true 인 rule로 replace 한다.
 * - `to` 는 문자열 또는 `(ctx) => string` 함수 모두 허용한다.
 * - replace 시 현재 URL의 쿼리(UTM 등)를 이동 경로에 자동으로 붙인다.
 *
 * ## 주의
 * - `isChecking`이 true인 동안 contact는 공간을 유지하며 숨기고, submit은 렌더하지 않는다.
 * - rules 를 인라인 배열로 넘기면 매 렌더마다 재생성되어 무한루프 가능 →
 *   useMemo 또는 컴포넌트 외부 상수로 선언할 것
 */

import { useEffect, useRef, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { pathWithQueryString } from '@publishing/helpers/pathWithQuery';

const SESSION_KEY = 'ff_event_leads';

/** 모든 이벤트 공통 QA 우회 파라미터 — 예: `/event/ff-secret10thw?mktad` */
export const MKTAD_BYPASS_PARAM = 'mktad';

function isMktadBypass(searchParams: URLSearchParams): boolean {
  return searchParams.has(MKTAD_BYPASS_PARAM);
}

function isNumericBypassParam(searchParams: URLSearchParams, param: string): boolean {
  const value = searchParams.get(param);
  return value !== null && Number(value) > 0 && !Number.isNaN(Number(value));
}

function readLeads(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '[]');
  } catch {
    return [];
  }
}

/** slug를 ff_event_leads 배열에 추가한다 (중복 방지) */
export function addEventLead(slug: string): void {
  if (typeof window === 'undefined') return;
  const leads = readLeads();
  if (!leads.includes(slug)) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...leads, slug]));
  }
}

export type LeadGateContext = {
  /** ff_event_leads 배열 */
  leads: string[];
  /** 현재 pathname */
  pathname: string;
  /** 임의의 sessionStorage 값 읽기 (custom rules 용) */
  getSessionValue: (key: string) => string | null;
  /** 현재 URL의 쿼리 파라미터 */
  searchParams: URLSearchParams;
  /** ?mktad 또는 bypassParam 우회 시 true — 세션 게이트 우회 상태 */
  isLeadBypassed: boolean;
};

export type LeadGateRule = {
  when: (ctx: LeadGateContext) => boolean;
  /** 이동할 경로. 동적 경로가 필요하면 (ctx) => string 함수 사용 */
  to: string | ((ctx: LeadGateContext) => string);
};

export type UseEventLeadGateOptions = {
  /** 이벤트 고유 식별자. sessionStorage에 저장되는 문자열 */
  slug: string;
  /** contact 페이지 경로. 기본값: `/event/${slug}-contact` */
  contactPath?: string;
  /** 결과 페이지 경로. 기본값: `/event/${slug}-submit` */
  resultPath?: string;
  /**
   * 커스텀 분기 규칙 배열.
   * 지정하면 기본 규칙을 완전히 대체한다.
   * 컴포넌트 외부 상수나 useMemo로 선언해야 무한루프를 막을 수 있다.
   */
  rules?: LeadGateRule[];
  /**
   * 이 URL 파라미터가 양수 숫자로 존재하면 세션 기반 default rules를 건너뛴다.
   * QA·검수용 우회에 사용. 커스텀 rules는 bypass 여부와 무관하게 동작한다.
   * ctx.isLeadBypassed 로 rules 안에서 분기 가능.
   */
  bypassParam?: string;
};

export function useEventLeadGate({
  slug,
  contactPath,
  resultPath,
  rules,
  bypassParam,
}: UseEventLeadGateOptions) {
  const router = useRouter();
  const pathname = usePathname();
  const nextSearchParams = useSearchParams();
  const [isChecking, setIsChecking] = useState(true);
  const [leads, setLeads] = useState<string[]>([]);
  const rulesRef = useRef(rules);

  useEffect(() => {
    rulesRef.current = rules;
  }, [rules]);

  const resolvedContactPath = contactPath ?? `/event/${slug}-contact`;
  const resolvedResultPath = resultPath ?? `/event/${slug}-submit`;

  useEffect(() => {
    const current = readLeads();

    const searchParams = new URLSearchParams(nextSearchParams.toString());

    const isLeadBypassed =
      isMktadBypass(searchParams) ||
      (bypassParam !== undefined && isNumericBypassParam(searchParams, bypassParam));

    const ctx: LeadGateContext = {
      leads: current,
      pathname,
      getSessionValue: (key) => sessionStorage.getItem(key),
      searchParams,
      isLeadBypassed,
    };

    const defaultRules: LeadGateRule[] = isLeadBypassed
      ? []
      : [
          // contact 페이지인데 이미 완료 → 결과로
          {
            when: ({ leads, pathname }) =>
              pathname === resolvedContactPath && leads.includes(slug),
            to: resolvedResultPath,
          },
          // 결과 페이지인데 아직 미완료 → contact로
          {
            when: ({ leads, pathname }) =>
              pathname === resolvedResultPath && !leads.includes(slug),
            to: resolvedContactPath,
          },
        ];

    const activeRules: LeadGateRule[] = rulesRef.current ?? defaultRules;

    for (const rule of activeRules) {
      if (rule.when(ctx)) {
        const target = typeof rule.to === 'function' ? rule.to(ctx) : rule.to;
        router.replace(pathWithQueryString(target, ctx.searchParams));
        return;
      }
    }

    const timerId = window.setTimeout(() => {
      setLeads(current);
      setIsChecking(false);
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [pathname, router, slug, resolvedContactPath, resolvedResultPath, nextSearchParams, bypassParam]);

  // Next는 본문이 나오기 전에 스크롤 처리를 끝내고, 긴 결과 페이지가 붙으면
  // 브라우저 앵커링이 이전 위치를 복원한다. 본문이 그려진 뒤에 다시 올린다.
  useEffect(() => {
    if (isChecking || pathname !== resolvedResultPath) return;
    window.scrollTo(0, 0);
  }, [isChecking, pathname, resolvedResultPath]);

  const addLead = () => {
    addEventLead(slug);
    setLeads(readLeads());
  };

  return {
    /** true인 동안 리다이렉트 판단 중 — 이 값이 true면 화면 렌더 보류 권장 */
    isChecking,
    /** 현재 세션의 전체 이벤트 완료 목록 */
    leads,
    /** 이 slug의 리드가 완료됐는지 여부 */
    isLeadCompleted: leads.includes(slug),
    /** 폼 제출 성공 시 호출. slug를 세션에 추가한다 */
    addLead,
  };
}
